// Lote G (M51): lectura y escritura de la matriz de EPP por puesto y del procedimiento de adquisición de EPP (PO 21).
// Igual que los procedimientos de GT 37 (M49): el borrador en epp_procedures y cada versión aprobada en
// sst_document_versions (tipo procedimiento_epp), firmada por el responsable de SSO en pantalla o en papel con el mismo
// aprobarVersion de los informes y procedimientos. La base: la versión aprobada no se modifica ni se borra; la fecha y
// el número los pone el servidor. Aquí solo se traducen los errores a mensajes para el usuario.

import { supabase } from '../../supabaseClient';
import { aprobarVersion, type FirmaAprobacion, type PapelAprobacion } from '../informes-puesto/ip-datos';
import type { FilaVersion } from '../informes-puesto/ip-tipos';
import {
  contenidoProcedimiento, hashProcedimiento, type EppMin, type FilaMatriz, type FilaMatrizNueva, type FilaProcedimientoEpp,
  type IperEppMin, type PuestoMin, type TrabajadorMin,
} from './epp-po21';

export interface DatosMatrizEpp {
  puestos: PuestoMin[];
  matriz: FilaMatriz[];
  iper: IperEppMin[];
  trabajadores: TrabajadorMin[];
  /** El borrador (uno por empresa), o null si todavía no se creó. */
  procedimiento: FilaProcedimientoEpp | null;
  /** Versiones aprobadas, de la más nueva a la más vieja. */
  versiones: FilaVersion[];
}

function mensaje(e: { code?: string; message: string }, accion: string): string {
  if (e.code === '42501' || /row-level security|permission denied/i.test(e.message)) return 'Solo el administrador o el gerente pueden hacer este cambio.';
  if (e.code === '42P01' || e.code === 'PGRST205') return 'Todavía no se puede guardar: falta actualizar la base de datos. Avisa al soporte de StorSafe.';
  if (e.code === '23505') return 'Ese EPP ya está en la matriz de este puesto.';
  return `No se pudo ${accion}: ${e.message}`;
}

/** null si la base no tiene la M51 (la pestaña lo dice). */
export async function cargarMatrizEpp(companyId: string): Promise<DatosMatrizEpp | null> {
  const [{ data: puestos, error: e1 }, { data: matriz, error: e2 }, { data: iper }, { data: trabajadores }, { data: procs, error: e3 },
    { data: versiones, error: e4 }] = await Promise.all([
    supabase.from('job_positions').select('id, name, created_at').eq('company_id', companyId).order('name'),
    supabase.from('epp_position_matrix').select('id, company_id, position_id, epp_id, specification, replacement_months, observation, origin, updated_at').eq('company_id', companyId),
    supabase.from('iper_assessments').select('position_id, medida_epp').eq('company_id', companyId).not('position_id', 'is', null),
    supabase.from('workers').select('position_id, status').eq('company_id', companyId),
    supabase.from('epp_procedures').select('id, company_id, body, created_at, updated_at').eq('company_id', companyId),
    supabase.from('sst_document_versions').select('*').eq('company_id', companyId).eq('doc_type', 'procedimiento_epp').order('version', { ascending: false }),
  ]);
  if (e1) throw new Error(mensaje(e1, 'leer los puestos'));
  if (e2 || e3 || e4) return null;
  return {
    puestos: (puestos ?? []) as PuestoMin[], matriz: (matriz ?? []) as FilaMatriz[], iper: (iper ?? []) as IperEppMin[],
    trabajadores: (trabajadores ?? []) as TrabajadorMin[], procedimiento: ((procs ?? []) as FilaProcedimientoEpp[])[0] ?? null,
    versiones: (versiones ?? []) as FilaVersion[],
  };
}

export async function agregarFilas(filas: FilaMatrizNueva[]): Promise<void> {
  if (filas.length === 0) return;
  const { error } = await supabase.from('epp_position_matrix').insert(filas);
  if (error) throw new Error(mensaje(error, 'agregar a la matriz'));
}

export async function editarFila(id: string, cambios: Pick<FilaMatriz, 'specification' | 'replacement_months' | 'observation'>): Promise<void> {
  const { error } = await supabase.from('epp_position_matrix').update(cambios).eq('id', id);
  if (error) throw new Error(mensaje(error, 'guardar la fila'));
}

export async function borrarFila(id: string): Promise<void> {
  const { error } = await supabase.from('epp_position_matrix').delete().eq('id', id);
  if (error) throw new Error(mensaje(error, 'quitar la fila'));
}

/** Crea el borrador del procedimiento (uno por empresa) con el texto base. */
export async function crearProcedimiento(companyId: string, body: string): Promise<void> {
  const { error } = await supabase.from('epp_procedures').insert({ company_id: companyId, body });
  if (error) throw new Error(error.code === '23505' ? 'La empresa ya tiene su procedimiento: recarga la página.' : mensaje(error, 'crear el procedimiento'));
}

export async function guardarTexto(id: string, body: string): Promise<void> {
  const { data, error } = await supabase.from('epp_procedures').update({ body }).eq('id', id).select('id');
  if (error) throw new Error(mensaje(error, 'guardar el texto'));
  if (!data?.length) throw new Error('Solo el administrador o el gerente pueden hacer este cambio.');
}

/**
 * Aprueba una versión nueva con el texto guardado y la matriz de hoy, congelados con su SHA-256, firmada por el
 * responsable de SSO en pantalla (trazo, nombre y cédula) o en papel (escaneo). La base pone el número, la fecha y la
 * hora; la versión aprobada ya no se edita ni se borra.
 */
export async function aprobarProcedimiento(p: FilaProcedimientoEpp, empresa: string, d: Pick<DatosMatrizEpp, 'matriz' | 'puestos'>, catalogo: EppMin[],
  firma: FirmaAprobacion | PapelAprobacion): Promise<FilaVersion> {
  const contenido = contenidoProcedimiento(p, empresa, d.matriz, d.puestos, catalogo);
  const hash = await hashProcedimiento(contenido);
  return aprobarVersion(p.company_id, { tipo: 'procedimiento_epp', procedimientoEppId: p.id }, contenido, hash, firma);
}
